import {parseDate} from 'shared/web';

import type {RentalResourceCalendarEvent} from '../types/RentalResourceCalendarEvent';

export type GroupedRentalResourceCalendarEvent = RentalResourceCalendarEvent & {
  originalEvents: RentalResourceCalendarEvent[];
};

/**
 * Groups calendar events by their groupId property. Events with the same groupId
 * are combined into a single grouped event that spans from the earliest start
 * to the latest end date of all events in the group.
 *
 * @param events - Array of calendar events to group
 * @returns Array of grouped events, where each group represents one or more original events
 */
export function groupEventsByGroupId(events: RentalResourceCalendarEvent[]) {
  const groups = new Map<string, RentalResourceCalendarEvent[]>();

  events.forEach((event) => {
    const key = event.groupId || event.id;
    const group = groups.get(key) || [];
    group.push(event);
    groups.set(key, group);
  });

  return Array.from(groups.entries()).map(([groupKey, groupEvents]) => {
    const sortedEvents = groupEvents.sort((a, b) => a.start.getTime() - b.start.getTime());
    const firstEvent = sortedEvents[0];
    const startTimes = groupEvents.map((e) => e.start.getTime());
    const endTimes = groupEvents.map((e) => e.end.getTime());

    const groupedEvent = {
      ...firstEvent,
      id: groupKey,
      start: parseDate(Math.min(...startTimes)),
      end: parseDate(Math.max(...endTimes)),
      originalEvents: sortedEvents,
    } as GroupedRentalResourceCalendarEvent;

    // Remove event-specific properties that don't make sense for a grouped event
    delete (groupedEvent as any).isActualEventEnd;
    delete (groupedEvent as any).alternative;

    return groupedEvent;
  });
}
